import { useState, useEffect, useContext, useRef } from "react";
import { Grid, Typography, CircularProgress, IconButton } from "@mui/material";
import AttendeeCard from "../RoundComponents/AttendeeCard";
import AttendeePage from "../RoundComponents/AttendeePage";
import { AppContext } from "../../contexts/AppContext";
import request from "../../utilities/requests";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MixerHelp from "./components/MixerHelp";
import FilterListIcon from "@mui/icons-material/FilterList";
import MixerFilters from "./components/MixerFilters";
import { toast } from "../../utilities/SnackBarUtils";

const MixerEvent = ({ eventId }) => {
  const { event, mixerAttendees, setMixerAttendees } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [attendees, setAttendees] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedAttendee, setSelectedAttendee] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState(null);

  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const loadingRef = useRef(false);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    getAttendees(1);

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMoreRef.current && !loadingRef.current) {
          const nextPage = pageRef.current + 1;
          getAttendees(nextPage);
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const getAttendees = async (nextPage = 1, filtersOverride = undefined) => {
    loadingRef.current = true;

    if (nextPage === 1 && mixerAttendees.length === 0) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    const filtersToUse =
      filtersOverride !== undefined ? filtersOverride : activeFilters;

    let result;

    // If filters are active → call filtered endpoint
    if (filtersToUse && Object.keys(filtersToUse).length) {
      result = await request("pairingevent/mixerattendees/filter", "post", {
        eventId,
        filters: filtersToUse,
        page: nextPage,
        limit: 50,
      });
    } else {
      // Otherwise call normal endpoint
      result = await request(
        `pairingevent/mixerattendees?eventId=${eventId}&page=${nextPage}&limit=50`,
      );
    }

    if (result.success) {
      if (nextPage === 1) {
        setMixerAttendees(result.attendees);
      } else {
        setMixerAttendees((prev) => [...prev, ...result.attendees]);
      }

      setPage(nextPage);
      setHasMore(result.pagination?.hasMore);
      pageRef.current = nextPage;
      hasMoreRef.current = result.pagination?.hasMore;
      console.log("mixer attendees refreshed");
    }

    setLoading(false);
    setLoadingMore(false);
    loadingRef.current = false;
  };

  const handleSelectAttendee = (attendee) => {
    setSelectedAttendee(attendee);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setActiveFilters(null);
    // getAttendees();
    setDialogOpen(false);
    setSelectedAttendee(null);
  };

  const handleOpenHelp = () => setHelpOpen(true);
  const handleCloseHelp = () => setHelpOpen(false);
  const handleOpenFilters = () => setFiltersOpen(true);
  const handleCloseFilters = () => setFiltersOpen(false);

  const handleClearAllFilters = async () => {
    try {
      setLoading(true);
      setActiveFilters(null);
      setFiltersOpen(false);
      setMixerAttendees([]);
      pageRef.current = 1;
      await getAttendees(1, null);
    } catch (e) {
      console.error(`Error clearing filters: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFilteredResults = async (filters) => {
    // Clear current list and show main loading spinner
    setMixerAttendees([]);
    setLoading(true);
    pageRef.current = 1;

    const payload = {
      eventId,
      filters,
      page: 1,
      limit: 50,
    };

    console.log("Mixer filter payload:", payload);

    const result = await request(
      "pairingevent/mixerattendees/filter",
      "post",
      payload,
    );

    if (result?.success) {
      setMixerAttendees(result.attendees);
      setHasMore(result.pagination?.hasMore);
      pageRef.current = 1;
      hasMoreRef.current = result.pagination?.hasMore;

      // Persist active filters
      setActiveFilters(filters);

      // Close dialog after applying
      setFiltersOpen(false);

      setLoading(false);
    } else {
      setLoading(false);
      toast.error(result.error);
    }
  };

  if (selectedAttendee) {
    return (
      <AttendeePage
        attendee={selectedAttendee}
        open={dialogOpen}
        close={handleClose}
        pairingEventType={event?.pairingEventType}
      />
    );
  }

  const renderAttendees = () => {
    if (loading) {
      return (
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginTop: 100,
          }}
        >
          <CircularProgress size={80} />
        </Grid>
      );
    }
    if (!loading && mixerAttendees.length === 0) {
      return (
        <Grid item xs={12} style={{ marginTop: 100 }}>
          <Typography align="center" color="textSecondary">
            Your guest list may still be calculating. Please wait a minute and
            try refreshing the screen.
            <br />
            If after a few minutes you still do not see anyone, please contact
            your host.
          </Typography>
        </Grid>
      );
    }

    return (
      <>
        {mixerAttendees.map((attendee) => (
          <AttendeeCard
            key={attendee?._id}
            attendee={attendee}
            handleSelect={handleSelectAttendee}
          />
        ))}
        {loadingMore && (
          <Grid
            item
            xs={12}
            style={{ display: "flex", justifyContent: "center", marginTop: 20 }}
          >
            <CircularProgress />
          </Grid>
        )}
      </>
    );
  };

  return (
    <>
      <Grid container spacing={3} style={{ padding: 10, paddingBottom: 96 }}>
        <Grid item xs={12}>
          <Grid container alignItems="center">
            <Grid item xs={4}>
              <IconButton onClick={handleOpenHelp}>
                <HelpOutlineIcon />
              </IconButton>
            </Grid>

            <Grid item xs={4} style={{ textAlign: "center" }}>
              <Typography variant="h5">GUEST LIST</Typography>
            </Grid>

            <Grid item xs={4} style={{ textAlign: "right" }}>
              <IconButton
                onClick={handleOpenFilters}
                color={
                  activeFilters && Object.keys(activeFilters).length
                    ? "primary"
                    : "default"
                }
              >
                <FilterListIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
        {renderAttendees()}
      </Grid>

      <MixerHelp open={helpOpen} onClose={handleCloseHelp} />

      <MixerFilters
        open={filtersOpen}
        onClose={handleCloseFilters}
        eventId={eventId}
        onViewResults={handleFilteredResults}
        onClearAllFilters={handleClearAllFilters}
      />

      <div ref={loadMoreRef} style={{ height: 1 }} />
    </>
  );
};

export default MixerEvent;
